import React, { useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { Icon } from '@iconify/react';
import { useToast } from './ui/ToastProvider';
interface Transaction {
  id: string;
  mtoRef: string;
  bankRef: string;
  mtoName: string;
  remitterName: string;
  beneficiaryName: string;
  beneficiaryIBAN: string;
  amountFCY: string;
  currencyFCY: string;
  amountPKR: string;
  purposeCode: string;
  purposeText: string;
  payoutType: string;
  corridor: string;
  createdAt: string;
  ePRCNo?: string;
  ePRCDate?: string;
}
interface BulkEPRCModalProps {
  open: boolean;
  onClose: () => void;
  transactions: Transaction[];
}
const BulkEPRCModal: React.FC<BulkEPRCModalProps> = ({
  open,
  onClose,
  transactions
}) => {
  const {
    showToast
  } = useToast();
  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = () => {
    if (printRef.current) {
      const printWindow = window.open('', '', 'height=600,width=800');
      if (printWindow) {
        printWindow.document.write('<html><head><title>Bulk e-PRC Certificates</title>');
        printWindow.document.write('<style>{`');
        printWindow.document.write(`
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          body { 
            font-family: Arial, sans-serif; 
            margin: 0;
          }
          .page {
            page-break-after: always;
            page-break-inside: avoid;
            padding: 20mm;
            min-height: 100vh;
          }
          .page:last-child {
            page-break-after: auto;
          }
          .certificate-container {
            border: 2px solid #000 !important;
            padding: 30px;
          }
          table { 
            border-collapse: collapse !important; 
            width: 100% !important; 
            margin-bottom: 15px !important;
            border: 1px solid #000 !important;
          }
          td, th { 
            border: 1px solid #000 !important; 
            padding: 8px !important; 
            font-size: 11px !important;
          }
          .header { 
            text-align: center; 
            margin-bottom: 20px; 
            padding-bottom: 15px;
            border-bottom: 2px solid #000 !important;
          }
          .header h1 { 
            font-size: 18px; 
            margin: 0 0 5px 0;
            font-weight: bold;
          }
          .header p { 
            font-size: 13px; 
            margin: 0;
          }
          .section-header {
            background-color: #f5f5f5 !important;
            font-weight: bold !important;
          }
          .note {
            font-size: 10px;
            font-style: italic;
            margin-top: 10px;
          }
          .cert-text {
            font-size: 11px;
            line-height: 1.6;
            margin-bottom: 15px;
          }
          @page {
            size: A4 portrait;
            margin: 0;
          }
          @media print {
            body {
              margin: 0;
            }
            .page {
              page-break-after: always;
              page-break-inside: avoid;
              padding: 20mm;
              min-height: 100vh;
            }
            .page:last-child {
              page-break-after: auto;
            }
            .certificate-container {
              border: 2px solid #000 !important;
            }
            table, td, th {
              border: 1px solid #000 !important;
            }
            .section-header {
              background-color: #f5f5f5 !important;
            }
          }
        `);
        printWindow.document.write('`}</style></head><body>');
        printWindow.document.write(printRef.current.innerHTML);
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
      }
    }
  };
  const handleDownload = () => {
    showToast('Use Print button and select "Save as PDF" in the print dialog', 'info', 'Download as PDF', 5000);
    handlePrint();
  };
  // Extract country from corridor
  const getRemitterCountry = (corridor: string) => {
    const parts = corridor.split('-');
    return parts[0] || 'N/A';
  };
  // Get country code
  const getCountryCode = (corridor: string) => {
    const country = getRemitterCountry(corridor);
    const codes: Record<string, string> = {
      USA: 'US',
      UAE: 'AE',
      UK: 'GB',
      SAU: 'SA'
    };
    return codes[country] || country;
  };
  return <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{
    sx: {
      borderRadius: 2,
      maxHeight: '90vh'
    }
  }} data-id="element-212" data-mp="2ovx9ltcfpvkk">
      <DialogTitle sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      pb: 2
    }} data-id="element-213" data-mp="2aaj3qyhwz9rk">
        <Box data-id="element-214" data-mp="2k7dzxaqpsy2o">
          <Typography variant="h6" fontWeight={600} data-id="element-215" data-mp="2k7xkkz7r20ke">
            Bulk e-PRC Certificate
          </Typography>
          <Typography variant="body2" color="text.secondary" data-id="element-216" data-mp="2f9m9ym9mrm14">
            {transactions.length} transactions selected
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" data-id="element-217" data-mp="2o8zkzjdekiny">
          <Icon icon="lucide:x" width={20} height={20} data-id="element-218" data-mp="2o6dezo65ub7u" />
        </IconButton>
      </DialogTitle>
      <DialogContent data-id="element-219" data-mp="32z6xzjkn59h9">
        <Box ref={printRef} data-id="element-220" data-mp="2xyasx2ojzha9">
          {transactions.map((transaction, index) => <Box key={transaction.id} className="page" data-id="element-221" data-mp="2y0dijllsn8eg" data-mpi={index}>
              <Box className="certificate-container" sx={{
            p: 4,
            border: '2px solid #000',
            borderRadius: 0,
            mb: index < transactions.length - 1 ? 4 : 0
          }} data-id="element-222" data-mp="3byllca5ih3xj">
                {/* Header */}
                <Box className="header" sx={{
              textAlign: 'center',
              mb: 3,
              pb: 2,
              borderBottom: '2px solid #000'
            }} data-id="element-223" data-mp="3jwdrcqtrjp69">
                  <Typography component="h1" sx={{
                fontSize: '1.25rem',
                fontWeight: 700,
                mb: 1
              }} data-id="element-224" data-mp="1jpbjqvc499v">
                    Electronic-Proceeds Realization Certificate (e-PRC)
                  </Typography>
                  <Typography component="p" sx={{
                fontSize: '0.9rem'
              }} data-id="element-225" data-mp="2da1ez5ocswf7">
                    Personal / Corporate Customer
                  </Typography>
                </Box>

                {/* e-PRC Number and Date */}
                <TableContainer sx={{
              mb: 3
            }} data-id="element-226" data-mp="umrs3y0f5fh2">
                  <Table size="small" sx={{
                border: '1px solid #000'
              }} data-id="element-227" data-mp="qmho2qw6sz4u">
                    <TableBody data-id="element-228" data-mp="1ide05pafazr2">
                      <TableRow data-id="element-229" data-mp="1icccmlc80ksg">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      width: '30%',
                      fontSize: '0.85rem',
                      p: 1
                    }} data-id="element-230" data-mp="1iajg3yvbngy6">
                          e-PRC No. :
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.85rem',
                      p: 1
                    }} data-id="element-231" data-mp="3p3ljz578mc1y">
                          {transaction.ePRCNo}
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-232" data-mp="1j4dtiskkod97">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      p: 1
                    }} data-id="element-233" data-mp="1o0kqhvuzqef0">
                          Date of e-PRC :
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.85rem',
                      p: 1
                    }} data-id="element-234" data-mp="26zox6xz27w3i">
                          {new Date(transaction.ePRCDate || '').toLocaleDateString('en-GB')}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Remitter and Beneficiary Details */}
                <TableContainer sx={{
              mb: 3
            }} data-id="element-235" data-mp="srqxrimf5gg5">
                  <Table size="small" sx={{
                border: '1px solid #000'
              }} data-id="element-236" data-mp="537r17j0pbq6">
                    <TableBody data-id="element-237" data-mp="e3cux7p05rzl">
                      <TableRow data-id="element-238" data-mp="e4nvyzr2cln0">
                        <TableCell colSpan={2} className="section-header" sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      backgroundColor: '#f5f5f5',
                      fontSize: '0.85rem',
                      p: 1
                    }} data-id="element-239" data-mp="a1izbdtnze6a">
                          Remitter's Detail:
                        </TableCell>
                        <TableCell colSpan={2} className="section-header" sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      backgroundColor: '#f5f5f5',
                      fontSize: '0.85rem',
                      p: 1
                    }} data-id="element-240" data-mp="21vp250fs79i1">
                          Beneficiary's Detail:
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-241" data-mp="3dbnfhghzshp2">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      width: '25%',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-242" data-mp="3dam5lfcpq18x">
                          Name of Person/ Entity
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      width: '25%',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-243" data-mp="3pmojpwgot8a2">
                          {transaction.remitterName}
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      width: '25%',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-244" data-mp="36tu2swzi7637">
                          Name of Person/ Entity
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      width: '25%',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-245" data-mp="2n98j5alkazi0">
                          {transaction.beneficiaryName}
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-246" data-mp="1zi3590z6mhac">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-247" data-mp="1zj4fx4cad1rj">
                          NICOP/ Passport No./ CNIC/ POC/ Entity Registration
                          No./ Any other Unique ID
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-248" data-mp="3rgnsvejyj4fj">
                          N/A
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-249" data-mp="3ngneftwuyt8h">
                          NICOP/ CNIC/ Passport No./ NTN
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-250" data-mp="9zmqjh4aqkla">
                          N/A
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-251" data-mp="359v4bq1r38y6">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-252" data-mp="359latshhyjnq">
                          IBAN / Bank Account No. (if applicable)
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-253" data-mp="3oyvshc0szeoh">
                          N/A
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-254" data-mp="28l5lnxiaucb5">
                          Name of the Bank
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-255" data-mp="23dpbhdxrbvxx">
                          {transaction.beneficiaryIBAN.substring(4, 8)} Bank
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-256" data-mp="2m6jjtp0vkpt6">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-257" data-mp="2m35j0l7v0onu">
                          Name of Remitting (originating) Financial Institution
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-258" data-mp="jqj4a9tsyaso">
                          {transaction.mtoName}
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-259" data-mp="17n5kb3fsw9pp">
                          IBAN / Bank Account No.
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-260" data-mp="1qfzor3fssjfo">
                          {transaction.beneficiaryIBAN}
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-261" data-mp="1w1i48m86z3o2">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-262" data-mp="1w0720quea3z2">
                          Name of Country
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-263" data-mp="1dbiia87ne4ov">
                          {getRemitterCountry(transaction.corridor)}
                        </TableCell>
                        <TableCell colSpan={2} sx={{
                      border: '1px solid #000'
                    }} data-id="element-264" data-mp="1ntf9o4hicrqu" />
                      </TableRow>
                      <TableRow data-id="element-265" data-mp="1ereere9qyhsc">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-266" data-mp="1eutvsmmze0we">
                          Country Code (as per ITRS)
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-267" data-mp="v2njbnn4ixbf">
                          {getCountryCode(transaction.corridor)}
                        </TableCell>
                        <TableCell colSpan={2} sx={{
                      border: '1px solid #000'
                    }} data-id="element-268" data-mp="2sr962efj19a8" />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Certification Text */}
                <Box className="cert-text" sx={{
              mb: 2,
              fontSize: '0.8rem',
              lineHeight: 1.6
            }} data-id="element-269" data-mp="2a22sf5tlb7va">
                  This is to certify that we have received remittance by the
                  order of the above mentioned Remitter and have paid the
                  equivalent Pak Rupees to the Beneficiary, as per the detail
                  given as below:
                </Box>

                {/* Transaction Details */}
                <TableContainer sx={{
              mb: 3
            }} data-id="element-270" data-mp="1vb3nc4ryr1gg">
                  <Table size="small" sx={{
                border: '1px solid #000'
              }} data-id="element-271" data-mp="17qjtxfagsiw5">
                    <TableBody data-id="element-272" data-mp="1bsyeq4xp1sq2">
                      <TableRow data-id="element-273" data-mp="1gqzvgj7ndfyd">
                        <TableCell colSpan={2} className="section-header" sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      backgroundColor: '#f5f5f5',
                      fontSize: '0.85rem',
                      p: 1
                    }} data-id="element-274" data-mp="1gq6szufq1cm1">
                          Transaction Detail:
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-275" data-mp="5btdpdz1a5hm">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      width: '50%',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-276" data-mp="5b1i435fs7rc">
                          Date of Realization of Proceeds
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-277" data-mp="3g09lniebxmhj">
                          {new Date(transaction.createdAt).toLocaleDateString('en-GB')}
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-278" data-mp="2s82quno95nk8">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-279" data-mp="2saf5oryprht2">
                          Amount of Remittance (in FCY)*
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-280" data-mp="23q2c82eok15k">
                          {transaction.currencyFCY} {transaction.amountFCY}
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-281" data-mp="2mkr2rk1oq917">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-282" data-mp="2rkwpclt3l5cf">
                          Rate of Conversion*
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-283" data-mp="13o5kh1ikb1j4">
                          {(parseFloat(transaction.amountPKR) / parseFloat(transaction.amountFCY)).toFixed(2)}
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-284" data-mp="yp2qcc44efpx">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-285" data-mp="yq46guzztsei">
                          Amount of Remittance (in PKR)
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-286" data-mp="30gw6i6yzkh9r">
                          {parseFloat(transaction.amountPKR).toFixed(2)}
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-287" data-mp="3jdnlw5beqc8j">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-288" data-mp="3od0i3go64vns">
                          Purpose Code (as per ITRS)
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-289" data-mp="6upiuxde59oh">
                          {transaction.purposeCode}
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-290" data-mp="1mfqc797l4m1t">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-291" data-mp="1hgn57j81o5r7">
                          Purpose of Remittance
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-292" data-mp="20f97v2j7m4x8">
                          {transaction.purposeText}
                        </TableCell>
                      </TableRow>
                      <TableRow data-id="element-293" data-mp="r766qbsobh4y">
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-294" data-mp="v7q51iozgqso">
                          Transaction Reference No.
                        </TableCell>
                        <TableCell sx={{
                      border: '1px solid #000',
                      fontSize: '0.8rem',
                      p: 1
                    }} data-id="element-295" data-mp="chgv1mmyfuqf">
                          {transaction.bankRef}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Footer Notes */}
                <Box sx={{
              mb: 2
            }} className="note" data-id="element-296" data-mp="27yrokeie3zuq">
                  <Box sx={{
                fontSize: '0.7rem',
                mb: 1,
                fontStyle: 'italic'
              }} data-id="element-297" data-mp="230yg1etm6tra">
                    [ The above transaction has been will be reported to SBP/
                    SBP-BSC in Schedule 54 / 03 of ITRS for the period : Nov
                    2025 (if applicable)]
                  </Box>
                  <Box sx={{
                fontSize: '0.7rem',
                fontWeight: 600
              }} data-id="element-298" data-mp="2ls97bx3958jp">
                    Note: This is a system-generated document. No signature is
                    required.
                  </Box>
                </Box>

                {/* Additional Note */}
                <Box className="note" sx={{
              fontSize: '0.65rem',
              fontStyle: 'italic'
            }} data-id="element-299" data-mp="11bmo59lsx6av">
                  * N/A in case the payment is received through Vostro account
                  of the remitting institution, and the rate of conversion has
                  been applied by the remitting institution abroad.
                </Box>
              </Box>
            </Box>)}
        </Box>
      </DialogContent>
      <DialogActions sx={{
      p: 3,
      pt: 2
    }} data-id="element-300" data-mp="16ygp2us5w1es">
        <Button variant="outlined" onClick={onClose} data-id="element-301" data-mp="1bvqmoxppfjdp">
          Close
        </Button>
        <Button variant="outlined" startIcon={<Icon icon="lucide:download" width={18} data-id="element-303" data-mp="1kvwsdxf5vf2b" />} onClick={handleDownload} data-id="element-302" data-mp="1fviliipaqb5v">
          Download PDF
        </Button>
        <Button variant="contained" startIcon={<Icon icon="lucide:printer" width={18} data-id="element-305" data-mp="2wb205fu5g3ss" />} onClick={handlePrint} data-id="element-304" data-mp="2rbfmj5aesljg">
          Print
        </Button>
      </DialogActions>
    </Dialog>;
};
export default BulkEPRCModal;